import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { Link } from 'react-router-dom'
import { api } from '../../../../convex/_generated/api'
import { Star, Loader2, ChevronLeft, ChevronRight, ExternalLink, CheckCircle2, ListChecks, Sparkles } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

const STAR_LABELS = ['', 'Not great', 'Needs work', 'Decent', 'Really good', 'Nailed it']

export default function AdminModFeedbackPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const queue = useQuery(
    api.exportFeedback.moderationQueueList,
    isAdmin ? { limit: 30 } : 'skip'
  )

  const submit = useMutation(api.exportFeedback.submitModerator)
  const generateSummary = useAction(api.exportFeedback.generateChatSummary)

  const [cursor, setCursor] = useState(0)
  const [rating, setRating] = useState(0)
  const [hoveredStar, setHoveredStar] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)

  const items = queue?.items ?? []
  const safeCursor = Math.min(cursor, Math.max(0, items.length - 1))
  const current = items[safeCursor] ?? null

  const chatSummary = useQuery(
    api.exportFeedback.getChatSummary,
    current ? { workflowId: current.workflowId } : 'skip'
  )
  const [summaryGenerating, setSummaryGenerating] = useState(false)
  const [summaryError, setSummaryError] = useState<string | null>(null)

  useEffect(() => {
    if (!current) return
    if (chatSummary === undefined) return
    if (chatSummary !== null) return
    let cancelled = false
    setSummaryGenerating(true)
    setSummaryError(null)
    generateSummary({ workflowId: current.workflowId })
      .catch((err) => {
        if (!cancelled) setSummaryError(err?.message || 'Failed to summarize chat')
      })
      .finally(() => {
        if (!cancelled) setSummaryGenerating(false)
      })
    return () => {
      cancelled = true
    }
  }, [current?.workflowId, chatSummary === null, generateSummary])

  useEffect(() => {
    setRating(0)
    setHoveredStar(0)
    setFeedback('')
  }, [current?.workflowId])

  useEffect(() => {
    if (!stripRef.current) return
    const node = stripRef.current.querySelector<HTMLElement>(`[data-cursor-idx="${safeCursor}"]`)
    if (node) node.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [safeCursor, current?.workflowId])

  const displayRating = hoveredStar || rating
  const canSubmit = !!current && rating > 0 && feedback.trim().length > 0 && !submitting

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  if (queue === undefined) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  function goPrev() {
    setCursor((c) => Math.max(0, c - 1))
  }

  function goNext() {
    setCursor((c) => Math.min(items.length - 1, c + 1))
  }

  async function handleSubmit() {
    if (!current || !canSubmit) return
    setSubmitting(true)
    try {
      await submit({
        workflowId: current.workflowId,
        rating,
        feedback: feedback.trim(),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const queueExhausted = !current
  const canPrev = safeCursor > 0
  const canNext = safeCursor < items.length - 1

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-neutral-500" />
            Feedback Queue
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Moderator quality review — exported videos awaiting your rating
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Stat
            label="Pending"
            value={
              queue.pendingCount === undefined
                ? items.length
                : `${queue.pendingCount}${queue.pendingCountCapped ? '+' : ''}`
            }
          />
          <Stat label="Reviewed" value={queue.reviewedCount ?? 0} />
        </div>
      </div>

      {queueExhausted ? (
        <div className="rounded-lg border border-neutral-200 bg-white py-16 flex flex-col items-center gap-3">
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          <div className="text-base font-medium text-neutral-900">Queue clear</div>
          <p className="text-sm text-neutral-500 max-w-xs text-center">
            No exported projects waiting for moderator feedback. New exports will appear here automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
            {/* Player */}
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden border border-neutral-200 bg-black aspect-video flex items-center justify-center">
                {current.finalVideoUrl ? (
                  <video
                    key={current.workflowId}
                    src={current.finalVideoUrl}
                    controls
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-sm text-neutral-500">No video URL</div>
                )}
              </div>

              {/* Prev / position / Next */}
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={!canPrev}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors',
                    canPrev
                      ? 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                      : 'border-neutral-100 bg-neutral-50 text-neutral-300 cursor-not-allowed'
                  )}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
                <div className="text-[11px] text-neutral-400 tabular-nums">
                  {safeCursor + 1} / {items.length}
                </div>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canNext}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors',
                    canNext
                      ? 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                      : 'border-neutral-100 bg-neutral-50 text-neutral-300 cursor-not-allowed'
                  )}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{current.title}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {current.userName || current.userEmail || 'unknown user'}
                    {current.aspectRatio && (
                      <>
                        <span className="text-neutral-300">·</span>
                        <span>{current.aspectRatio}</span>
                      </>
                    )}
                    <span className="text-neutral-300">·</span>
                    <span>exported {formatTime(current.updatedAt)}</span>
                  </div>
                </div>
                <Link
                  to={`/video/${current.shortcode || current.workflowId}`}
                  target="_blank"
                  className="text-[11px] text-blue-500 hover:underline flex items-center gap-1 shrink-0"
                >
                  Open project <ExternalLink className="h-3 w-3" />
                </Link>
              </div>

              {current.userRating && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-1">User rated</div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          size={12}
                          className={s <= current.userRating!.rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-200'}
                        />
                      ))}
                    </div>
                    {current.userRating.feedback && (
                      <span className="text-xs text-neutral-700 truncate">"{current.userRating.feedback}"</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Rating panel */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5 flex flex-col gap-5 self-start">
              <div>
                <div className="text-sm font-medium text-neutral-900">How did this turn out?</div>
                <p className="text-xs text-neutral-500 mt-1">
                  Rate the quality of the exported video and leave a short note for the team.
                </p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      onClick={() => {
                        setRating(star)
                        if (!feedback && textareaRef.current) {
                          setTimeout(() => textareaRef.current?.focus(), 80)
                        }
                      }}
                      className="group p-1 transition-transform duration-150 hover:scale-110 active:scale-95"
                    >
                      <Star
                        size={28}
                        className={cn(
                          'transition-all duration-200',
                          star <= displayRating
                            ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.4)]'
                            : 'fill-transparent text-neutral-300 group-hover:text-neutral-400'
                        )}
                      />
                    </button>
                  ))}
                </div>
                <span className={cn(
                  'text-xs tracking-wide h-4 transition-colors',
                  displayRating > 0 ? 'text-neutral-600' : 'text-transparent'
                )}>
                  {STAR_LABELS[displayRating] || ''}
                </span>
              </div>

              <textarea
                ref={textareaRef}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={
                  rating <= 2 && rating > 0
                    ? 'What fell short? Be specific — it helps.'
                    : rating >= 4
                      ? "What worked? Anything you'd still tweak?"
                      : 'What would have made this better?'
                }
                rows={5}
                className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-[13px] text-neutral-900 placeholder:text-neutral-400 resize-none focus:outline-none focus:border-neutral-400 focus:bg-white transition-colors"
              />

              <button
                type="button"
                disabled={!canSubmit}
                onClick={handleSubmit}
                className={cn(
                  'w-full h-10 rounded-lg text-[13px] font-medium tracking-tight transition-all',
                  canSubmit
                    ? 'bg-neutral-900 text-white hover:bg-neutral-800 active:scale-[0.99]'
                    : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                )}
              >
                {submitting ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>

          {/* Chat summary */}
          <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3.5">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-neutral-400 font-semibold mb-2">
              <Sparkles className="h-3 w-3" />
              Chat summary
            </div>
            {chatSummary === undefined ? (
              <div className="flex items-center gap-2 text-xs text-neutral-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            ) : chatSummary === null ? (
              summaryError ? (
                <div className="text-xs text-rose-500">{summaryError}</div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {summaryGenerating ? 'Summarizing chat…' : 'Preparing summary…'}
                </div>
              )
            ) : (
              <div className="text-[13px] leading-relaxed text-neutral-700 whitespace-pre-wrap">
                {chatSummary.summary}
              </div>
            )}
          </div>

          {/* Pending strip */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-neutral-400 font-semibold px-0.5">
              Up next
            </div>
            <div
              ref={stripRef}
              className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth"
            >
              {items.map((it, idx) => {
                const isCurrent = idx === safeCursor
                return (
                  <button
                    key={it.workflowId}
                    type="button"
                    data-cursor-idx={idx}
                    onClick={() => setCursor(idx)}
                    className={cn(
                      'group shrink-0 w-44 rounded-lg overflow-hidden border-2 transition-all bg-white text-left',
                      isCurrent
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-transparent hover:border-neutral-300'
                    )}
                  >
                    <div className="aspect-video bg-black overflow-hidden">
                      {it.thumbnailUrl ? (
                        <img
                          src={it.thumbnailUrl}
                          alt={it.title}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : it.finalVideoUrl ? (
                        <video
                          src={`${it.finalVideoUrl}#t=0.5`}
                          preload="metadata"
                          muted
                          playsInline
                          onLoadedMetadata={(e) => {
                            const v = e.currentTarget
                            if (v.currentTime < 0.5) {
                              try { v.currentTime = 0.5 } catch {}
                            }
                          }}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-500">
                          no video
                        </div>
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <div className={cn(
                        'text-xs font-medium truncate',
                        isCurrent ? 'text-blue-700' : 'text-neutral-900'
                      )}>
                        {it.title}
                      </div>
                      <div className="text-[10px] text-neutral-400 truncate">
                        {it.userName || it.userEmail || 'unknown'}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</div>
      <div className="text-sm font-semibold text-neutral-900 leading-tight">{value}</div>
    </div>
  )
}

function formatTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
